import type { ActivityContext, ActivityHandle } from '../../core/activity';
import { makeDraggable } from '../../shared/drag-drop';
import levelsData from './levels.json';

interface Product {
  emoji: string;
  name: string;
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  rounds: number;
  minPrice: number;
  maxPrice: number;
  paidOptions: number[];
  products: Product[];
}

interface BillKind {
  value: number;
  type: 'coin' | 'note';
}

const LEVELS = levelsData.levels as Level[];

// F2 (P-166): no modo adulto a compra é a do dia a dia, não a da lancheira.
const PRODUTOS_ADULTO: Product[] = [
  { emoji: '💡', name: 'Conta de luz' },
  { emoji: '🚌', name: 'Passagem de ônibus' },
  { emoji: '📱', name: 'Recarga do celular' },
  { emoji: '🍞', name: 'Pão da semana' },
  { emoji: '☕', name: 'Café' },
  { emoji: '🧺', name: 'Cesta básica' },
  { emoji: '💊', name: 'Remédio' },
  { emoji: '⛽', name: 'Combustível' },
];

const BILLS: BillKind[] = [
  { value: 1, type: 'coin' },
  { value: 2, type: 'note' },
  { value: 5, type: 'note' },
  { value: 10, type: 'note' },
  { value: 20, type: 'note' },
  { value: 50, type: 'note' },
  { value: 100, type: 'note' },
  { value: 200, type: 'note' },
];

function randInt(min: number, max: number): number {
  if (max < min) return min;
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function mountMoneyBack(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0]!;

  let roundIndex = 0;
  let product: Product = { emoji: '', name: '' };
  let price = 0;
  let paid = 0;
  let expectedChange = 0;
  let tray: number[] = [];
  let correctCount = 0;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Devolva o troco</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage money">
      <div class="feedback" aria-live="polite"></div>
      <p class="money__progress" aria-live="polite"></p>
      <div class="money__product">
        <div class="money__product-emoji"></div>
        <div class="money__product-info">
          <div class="money__product-name"></div>
          <div class="money__product-price"></div>
        </div>
      </div>
      <div class="money__paid-row" aria-live="polite"></div>
      <div class="money__tray-wrap">
        <div class="money__tray-label">Troco montado</div>
        <div class="money__tray" role="list" aria-label="Troco que você vai dar"></div>
        <div class="money__total">Total: <strong class="money__total-value">R$ 0</strong></div>
      </div>
      <div class="money__bills" role="group" aria-label="Moedas e notas disponíveis"></div>
      <button class="btn btn--primary money__ok" type="button">Dar troco</button>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.money__progress')!;
  const emojiEl = page.querySelector<HTMLElement>('.money__product-emoji')!;
  const nameEl = page.querySelector<HTMLElement>('.money__product-name')!;
  const priceEl = page.querySelector<HTMLElement>('.money__product-price')!;
  const paidRowEl = page.querySelector<HTMLElement>('.money__paid-row')!;
  const trayEl = page.querySelector<HTMLElement>('.money__tray')!;
  const totalEl = page.querySelector<HTMLElement>('.money__total-value')!;
  const billsEl = page.querySelector<HTMLElement>('.money__bills')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const okBtn = page.querySelector<HTMLButtonElement>('.money__ok')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  function renderBills(): void {
    billsEl.innerHTML = '';
    trayEl.dataset['dropTray'] = '1';
    BILLS.forEach((b) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = `money__bill money__bill--${b.type} money__bill--v${b.value}`;
      el.dataset.value = String(b.value);
      el.textContent = `R$ ${b.value}`;
      el.setAttribute('aria-label', `Adicionar ${b.type === 'coin' ? 'moeda' : 'nota'} de R$ ${b.value}`);
      el.addEventListener('click', () => addToTray(b.value));
      billsEl.appendChild(el);
      makeDraggable(el, {
        getDropTarget: (x, y) => {
          const e = document.elementFromPoint(x, y);
          return e?.closest<HTMLElement>('[data-drop-tray]') ?? null;
        },
        onDrop: () => addToTray(b.value),
      });
    });
  }

  function renderTray(): void {
    trayEl.innerHTML = '';
    if (tray.length === 0) {
      const empty = document.createElement('span');
      empty.className = 'money__tray-empty';
      empty.textContent = 'Troco vazio';
      trayEl.appendChild(empty);
    } else {
      tray.forEach((v, i) => {
        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'money__tray-chip';
        el.dataset.idx = String(i);
        el.textContent = `R$ ${v}`;
        el.setAttribute('aria-label', `Tirar R$ ${v} do troco`);
        el.addEventListener('click', () => removeFromTray(i));
        trayEl.appendChild(el);
      });
    }
    const total = tray.reduce((a, b) => a + b, 0);
    totalEl.textContent = `R$ ${total}`;
    totalEl.classList.toggle('money__total-value--match', total === expectedChange);
    totalEl.classList.toggle('money__total-value--over', total > expectedChange);
  }

  function addToTray(value: number): void {
    tray.push(value);
    ctx.audio.play('click');
    renderTray();
  }

  function removeFromTray(idx: number): void {
    tray.splice(idx, 1);
    ctx.audio.play('click');
    renderTray();
  }

  function pickPaid(price: number): number {
    // primeiro paidOption maior que price; se nenhum, último
    for (const p of level.paidOptions) if (p > price) return p;
    return level.paidOptions[level.paidOptions.length - 1]!;
  }

  function startRound(): void {
    const pool = ctx.publico === 'adulto' ? PRODUTOS_ADULTO : level.products;
    product = pool[randInt(0, pool.length - 1)]!;
    price = randInt(level.minPrice, level.maxPrice);
    paid = pickPaid(price);
    expectedChange = paid - price;
    tray = [];
    renderProgress();
    renderProduct();
    renderPaidRow();
    renderTray();
  }

  function renderProgress(): void {
    progressEl.textContent = `Compra ${roundIndex + 1} de ${level.rounds}`;
  }

  function renderProduct(): void {
    emojiEl.textContent = product.emoji;
    nameEl.textContent = product.name;
    priceEl.textContent = `R$ ${price}`;
  }

  function renderPaidRow(): void {
    paidRowEl.innerHTML = `O cliente pagou <strong>R$ ${paid}</strong> <span class="money__paid-arrow">→</span> troco = <strong>R$ ${expectedChange}</strong>`;
  }

  function showFeedback(kind: 'ok' | 'err', msg = ''): void {
    feedback.textContent = msg || (kind === 'ok' ? '✓' : '✗');
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 700);
  }

  function validate(): void {
    const total = tray.reduce((a, b) => a + b, 0);
    if (total < expectedChange) {
      showFeedback('err', 'Falta');
      ctx.audio.play('wrong');
      return;
    }
    if (total > expectedChange) {
      showFeedback('err', 'Passou');
      ctx.audio.play('wrong');
      return;
    }
    ctx.audio.play('correct');
    showFeedback('ok');
    correctCount++;
    roundIndex++;
    window.setTimeout(() => {
      if (roundIndex < level.rounds) startRound();
      else finishLevel();
    }, 700);
  }

  okBtn.addEventListener('click', validate);

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') { validate(); e.preventDefault(); }
    else if (e.key === 'Backspace' && tray.length > 0) { tray.pop(); renderTray(); e.preventDefault(); }
  };
  window.addEventListener('keydown', onKey);

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você deu o troco certo em ${correctCount} de ${level.rounds} compras.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) next.addEventListener('click', () => { window.location.hash = `/money_back/${level.level + 1}`; });
  }

  renderBills();
  startRound();

  return {
    unmount(): void {
      window.removeEventListener('keydown', onKey);
      ctx.root.innerHTML = '';
    },
  };
}
